import { Order, PaginatedResponse, PaginationParams } from '../types';
import { apiClient } from './api';

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

function unwrapPage<T>(res: { success: boolean; data?: PagePayload<T>; message?: string; error?: string }): PaginatedResponse<T> {
  if (!res.success || !res.data) {
    throw new Error(res.message || res.error || 'خطا در دریافت داده');
  }
  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 10,
    totalPages: res.data.totalPages ?? 1,
  };
}

export const orderService = {
  async getOrders(
    params: PaginationParams & { status?: string; paymentStatus?: string; q?: string },
  ): Promise<PaginatedResponse<Order>> {
    const res = await apiClient.get<PagePayload<Order>>('/admin/orders', {
      params: {
        page: params.page,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        status: params.status,
        paymentStatus: params.paymentStatus,
        q: params.q,
      },
    });
    return unwrapPage(res);
  },

  async getOrder(id: string): Promise<Order> {
    const res = await apiClient.get<Order>(`/admin/orders/${id}`);
    if (!res.success || !res.data) throw new Error(res.message || 'سفارش یافت نشد');
    return res.data;
  },

  async updateOrderStatus(
    id: string,
    status: string,
    extras?: { trackingNumber?: string; shippingNote?: string; shippingMethod?: string; shippedAt?: string },
  ): Promise<Order> {
    const res = await apiClient.patch<Order>(`/admin/orders/${id}/status`, {
      status,
      trackingNumber: extras?.trackingNumber ?? '',
      shippingNote: extras?.shippingNote ?? '',
      shippingMethod: extras?.shippingMethod ?? '',
      shippedAt: extras?.shippedAt ?? '',
    });
    if (!res.success || !res.data) throw new Error(res.message || 'به‌روزرسانی ناموفق بود');
    return res.data;
  },

  async updateOrderExpenses(
    id: string,
    shippingExpense: number,
    packagingCost: number,
    cogsTotal?: number,
  ): Promise<Order> {
    const res = await apiClient.patch<Order>(`/admin/orders/${id}/expenses`, {
      shippingExpense,
      packagingCost,
      ...(cogsTotal === undefined ? {} : { cogsTotal }),
    });
    if (!res.success || !res.data) throw new Error(res.message || 'ذخیره هزینه‌ها ناموفق بود');
    return res.data;
  },

  async addOrderNote(orderId: string, text: string): Promise<void> {
    const res = await apiClient.post(`/admin/orders/${orderId}/notes`, { text });
    if (!res.success) throw new Error(res.message || 'ثبت یادداشت ناموفق بود');
  },

  async cancelOrder(id: string, reason: string): Promise<Order> {
    const res = await apiClient.post<Order>(`/admin/orders/${id}/cancel`, { reason });
    if (!res.success || !res.data) throw new Error(res.message || 'لغو سفارش ناموفق بود');
    return res.data;
  },
};
